/**
 * Operational Telemetry
 *
 * Collects metrics on auth/bootstrap/health for observability.
 * All events logged to console (development) and sent to analytics backend (production).
 *
 * Metrics tracked:
 * - Login duration (form submit → authenticated)
 * - Logout duration (click logout → redirected)
 * - Bootstrap duration (initial load → session verified)
 * - Redis timeout frequency
 * - Degraded mode activation/recovery
 * - Runtime health poll failures
 * - Reconnect attempts
 */

interface TelemetryEvent {
  type: string
  timestamp: number
  duration?: number
  metadata?: Record<string, any>
}

class OperationalTelemetry {
  private events: TelemetryEvent[] = []
  private maxEvents = 100 // Keep last 100 events in memory

  private timers: Map<string, number> = new Map() // Track in-flight durations

  /**
   * Record auth/session event
   */
  recordAuthEvent(
    type: 'login-start' | 'login-complete' | 'login-error' | 'logout-start' | 'logout-complete' | 'logout-error',
    metadata?: Record<string, any>
  ) {
    const event: TelemetryEvent = {
      type,
      timestamp: Date.now(),
      metadata,
    }

    // Calculate duration if ending an event
    if (type === 'login-complete' || type === 'login-error') {
      const startTime = this.timers.get('login-start')
      if (startTime) {
        event.duration = Date.now() - startTime
        this.timers.delete('login-start')
      }
    } else if (type === 'logout-complete' || type === 'logout-error') {
      const startTime = this.timers.get('logout-start')
      if (startTime) {
        event.duration = Date.now() - startTime
        this.timers.delete('logout-start')
      }
    }

    // Start timer for start events
    if (type === 'login-start' || type === 'logout-start') {
      this.timers.set(type, Date.now())
    }

    this.logEvent(event)
  }

  /**
   * Record bootstrap event (initial session verification)
   */
  recordBootstrapEvent(
    type: 'bootstrap-start' | 'bootstrap-complete' | 'bootstrap-timeout' | 'bootstrap-error',
    metadata?: Record<string, any>
  ) {
    const event: TelemetryEvent = {
      type,
      timestamp: Date.now(),
      metadata,
    }

    if (type === 'bootstrap-complete' || type === 'bootstrap-timeout' || type === 'bootstrap-error') {
      const startTime = this.timers.get('bootstrap-start')
      if (startTime) {
        event.duration = Date.now() - startTime
        this.timers.delete('bootstrap-start')
      }
    } else if (type === 'bootstrap-start') {
      this.timers.set(type, Date.now())
    }

    this.logEvent(event)
  }

  /**
   * Record session status change
   */
  recordSessionStatusChange(from: string, to: string, reason?: string) {
    this.logEvent({
      type: 'session-status-change',
      timestamp: Date.now(),
      metadata: { from, to, reason },
    })
  }

  /**
   * Record operational health change (healthy → degraded → healthy)
   */
  recordHealthChange(from: 'healthy' | 'degraded' | 'error', to: 'healthy' | 'degraded' | 'error') {
    this.logEvent({
      type: 'health-status-change',
      timestamp: Date.now(),
      metadata: { from, to, duration_degraded_ms: this.calculateDegradedDuration() },
    })
  }

  /**
   * Record Redis timeout (failed health check)
   */
  recordRedisTimeout(attempt: number, latency_ms?: number) {
    this.logEvent({
      type: 'redis-timeout',
      timestamp: Date.now(),
      metadata: { attempt, latency_ms },
    })
  }

  /**
   * Record degraded mode activation
   */
  recordDegradedModeStart(reason: 'redis_unavailable' | 'service_error' | 'multiple_failures') {
    this.logEvent({
      type: 'degraded-mode-start',
      timestamp: Date.now(),
      metadata: { reason },
    })
    this.timers.set('degraded-start', Date.now())
  }

  /**
   * Record degraded mode recovery
   */
  recordDegradedModeEnd() {
    const startTime = this.timers.get('degraded-start')
    const duration = startTime ? Date.now() - startTime : undefined
    this.timers.delete('degraded-start')

    this.logEvent({
      type: 'degraded-mode-end',
      timestamp: Date.now(),
      metadata: { duration_ms: duration },
    })
  }

  /**
   * Record runtime health poll failure
   */
  recordHealthPollFailure(attempt: number, error: string) {
    this.logEvent({
      type: 'health-poll-failure',
      timestamp: Date.now(),
      metadata: { attempt, error },
    })
  }

  /**
   * Record reconnect attempt (manual or automatic)
   */
  recordReconnectAttempt(type: 'manual' | 'automatic', attempt: number) {
    this.logEvent({
      type: 'reconnect-attempt',
      timestamp: Date.now(),
      metadata: { reconnect_type: type, attempt },
    })
  }

  /**
   * Record session invalidation
   */
  recordSessionInvalidation(reason: string) {
    this.logEvent({
      type: 'session-invalidation',
      timestamp: Date.now(),
      metadata: { reason },
    })
  }

  /**
   * Record cache clearing operation
   */
  recordCacheClear(reason: string, duration_ms?: number) {
    this.logEvent({
      type: 'cache-clear',
      timestamp: Date.now(),
      metadata: { reason, duration_ms },
    })
  }

  /**
   * Record client-side DOM/runtime anomaly (non-fatal)
   */
  recordClientDomAnomaly(kind: string, metadata?: Record<string, any>) {
    this.logEvent({
      type: 'client-dom-anomaly',
      timestamp: Date.now(),
      metadata: { kind, ...metadata },
    })
  }

  /**
   * Get all recorded events (for debugging)
   */
  getEvents(): TelemetryEvent[] {
    return this.events
  }

  /**
   * Get summary metrics
   */
  getSummary() {
    const loginEvents = this.events.filter((e) => e.type.startsWith('login'))
    const logoutEvents = this.events.filter((e) => e.type.startsWith('logout'))
    const bootstrapEvents = this.events.filter((e) => e.type.startsWith('bootstrap'))
    const degradedEvents = this.events.filter((e) => e.type === 'degraded-mode-start')
    const redisTimeouts = this.events.filter((e) => e.type === 'redis-timeout')
    const healthPollFailures = this.events.filter((e) => e.type === 'health-poll-failure')

    const avgLoginDuration =
      loginEvents
        .filter((e) => e.duration)
        .reduce((sum, e) => sum + (e.duration || 0), 0) / Math.max(1, loginEvents.filter((e) => e.duration).length)

    const avgBootstrapDuration =
      bootstrapEvents
        .filter((e) => e.duration)
        .reduce((sum, e) => sum + (e.duration || 0), 0) /
      Math.max(1, bootstrapEvents.filter((e) => e.duration).length)

    return {
      total_events: this.events.length,
      login_count: loginEvents.filter((e) => e.type === 'login-complete').length,
      logout_count: logoutEvents.filter((e) => e.type === 'logout-complete').length,
      bootstrap_count: bootstrapEvents.filter((e) => e.type === 'bootstrap-complete').length,
      bootstrap_timeouts: bootstrapEvents.filter((e) => e.type === 'bootstrap-timeout').length,
      avg_login_duration_ms: Math.round(avgLoginDuration),
      avg_bootstrap_duration_ms: Math.round(avgBootstrapDuration),
      degraded_activations: degradedEvents.length,
      redis_timeouts: redisTimeouts.length,
      health_poll_failures: healthPollFailures.length,
    }
  }

  /**
   * Export all events for analytics
   */
  export(): TelemetryEvent[] {
    return JSON.parse(JSON.stringify(this.events))
  }

  /**
   * Clear all events
   */
  clear() {
    this.events = []
    this.timers.clear()
  }

  // Private helpers

  private logEvent(event: TelemetryEvent) {
    this.events.push(event)

    // Keep only last 100 events
    if (this.events.length > this.maxEvents) {
      this.events.shift()
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[telemetry] ${event.type}`, {
        duration_ms: event.duration,
        ...event.metadata,
      })
    }

    // TODO: Send to analytics backend in production
    // this.sendToAnalytics(event)
  }

  private calculateDegradedDuration(): number | undefined {
    // Calculate how long system has been degraded
    const degradedStart = this.events
      .filter((e) => e.type === 'degraded-mode-start')
      .pop()

    const degradedEnd = this.events
      .filter((e) => e.type === 'degraded-mode-end')
      .pop()

    if (degradedStart && !degradedEnd) {
      return Date.now() - degradedStart.timestamp
    } else if (degradedStart && degradedEnd) {
      return degradedEnd.timestamp - degradedStart.timestamp
    }

    return undefined
  }

  // private sendToAnalytics(event: TelemetryEvent) {
  //   // Implement analytics backend integration
  //   fetch('/api/telemetry', {
  //     method: 'POST',
  //     body: JSON.stringify(event),
  //     headers: { 'Content-Type': 'application/json' },
  //   }).catch(() => {
  //     // Telemetry failures are non-blocking
  //   })
  // }
}

// Singleton instance
export const operationalTelemetry = new OperationalTelemetry()

/**
 * Hook for accessing telemetry in components
 */
export function useOperationalTelemetry() {
  return {
    recordAuthEvent: operationalTelemetry.recordAuthEvent.bind(operationalTelemetry),
    recordBootstrapEvent: operationalTelemetry.recordBootstrapEvent.bind(operationalTelemetry),
    recordSessionStatusChange: operationalTelemetry.recordSessionStatusChange.bind(operationalTelemetry),
    recordHealthChange: operationalTelemetry.recordHealthChange.bind(operationalTelemetry),
    recordRedisTimeout: operationalTelemetry.recordRedisTimeout.bind(operationalTelemetry),
    recordDegradedModeStart: operationalTelemetry.recordDegradedModeStart.bind(operationalTelemetry),
    recordDegradedModeEnd: operationalTelemetry.recordDegradedModeEnd.bind(operationalTelemetry),
    recordReconnectAttempt: operationalTelemetry.recordReconnectAttempt.bind(operationalTelemetry),
    recordClientDomAnomaly: operationalTelemetry.recordClientDomAnomaly.bind(operationalTelemetry),
    getSummary: operationalTelemetry.getSummary.bind(operationalTelemetry),
    getEvents: operationalTelemetry.getEvents.bind(operationalTelemetry),
  }
}

/**
 * Expose telemetry on window for debugging
 */
if (typeof window !== 'undefined') {
  ;(window as any).__telemetry = operationalTelemetry
}
